import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  ConfigProvider,
  Form,
  Input,
  message,
  Modal,
  Radio,
  Row,
  Space,
  Steps,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useRequest } from "ahooks";
import "./App.css";
import "./global.less";
import { validatePassword } from "./passVerify";
import { encryptToVaultFormat } from "./utils";
import {
  getImageCaptchaApi,
  getPublicKeyApi,
  resetPasswordApi,
  sendVerificateCodeApi,
  verifyPasswordResetIdentityApi,
} from "./apis";

const fLayout = {
  labelCol: { span: 6 },
  wrapperCol: {
    xl: { span: 14 },
    lg: { span: 18 },
    md: { span: 24 },
  },
};

const successTextStyle = {
  overflow: "hidden",
  color: "#000000d9",
  fontWeight: "500",
  fontSize: "14px",
  lineHeight: 1.4,
  marginLeft: "16px",
};

const App = () => {
  const [form] = Form.useForm();
  const originComing = useRef(false);
  const timer = useRef<any>();
  const watchPhone = Form.useWatch("phone", form);
  const watchImgCode = Form.useWatch("imgCode", form);
  const watchPassword = Form.useWatch("password", form);

  const [currentStep, setCurrentStep] = useState(0);
  const [imgSrc, setImgSrc] = useState("");
  const [captchaId, setCaptchaId] = useState("");
  const [typeStatus, setTypeStatus] = useState("default");
  const [seconds, setSeconds] = useState<number | null>(null);
  const [verificationToken, setVerificationToken] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [resetExpiresIn, setResetExpiresIn] = useState(0);
  const [accountCandidates, setAccountCandidates] = useState<
    APITYPES.PasswordResetAccountCandidate[]
  >([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(
    null
  );
  const [successOpen, setSuccessOpen] = useState(false);
  const [cardWidth, setCardWidth] = useState("60%");
  const [formLayout, setFormLayout] = useState<any>(null);
  const [publicKeyObj, setPublicKeyObj] =
    useState<APITYPES.GetPublicKeyData | null>(null);

  const formItemLayout = formLayout === "horizontal" ? fLayout : null;
  const itemMarginBottom = useMemo(
    () => (formLayout === "horizontal" ? "defaulMargin" : "smallMaegin"),
    [formLayout]
  );
  const isInputEmail = useMemo(() => {
    const emailReg = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailReg.test(watchPhone || "");
  }, [watchPhone]);
  const selectedAccount = useMemo(
    () =>
      accountCandidates.find(
        (account) => account.accountId === selectedAccountId
      ),
    [accountCandidates, selectedAccountId]
  );
  const hasMultipleAccounts = accountCandidates.length > 1;

  const clearResetSession = () => {
    setResetToken("");
    setResetExpiresIn(0);
    setAccountCandidates([]);
    setSelectedAccountId(null);
  };

  const { run: runGetPublicKey } = useRequest(() => getPublicKeyApi(), {
    manual: true,
    onSuccess: (response) => {
      if (response.code === 0 && response.data) {
        setPublicKeyObj(response.data);
      } else {
        message.error(response.msg || "获取公钥失败");
      }
    },
    onError: (error) => message.error("获取公钥失败: " + error),
  });

  const { run: runGetImageCaptcha } = useRequest(
    () => getImageCaptchaApi({ timestamps: new Date().getTime() }),
    {
      manual: true,
      onSuccess: (response) => {
        if (response.code === 0 && response.data) {
          setImgSrc(response.data.picPath);
          setCaptchaId(response.data.captchaId);
        } else {
          message.error(response.msg || "获取图片验证码失败");
        }
      },
      onError: (error) => message.error("获取图片验证码失败: " + error),
    }
  );

  const { run: runSendVerificationCode, loading: sendCodeLoading } =
    useRequest(
      (data: APITYPES.SendVerificateCodeParams) =>
        sendVerificateCodeApi(data),
      {
        manual: true,
        onSuccess: (response) => {
          if (response.code === 0 && response.data?.token) {
            setVerificationToken(response.data.token);
            message.success("验证码发送成功");
          } else {
            message.error(response.msg || "验证码发送失败");
            setTypeStatus("default");
            setSeconds(null);
          }
        },
        onError: (error) => {
          message.error("发送验证码失败: " + error);
          setTypeStatus("default");
          setSeconds(null);
        },
      }
    );

  const {
    run: runVerifyPasswordResetIdentity,
    loading: identityVerifyLoading,
  } = useRequest(
    (data: APITYPES.PasswordResetVerifyParams) =>
      verifyPasswordResetIdentityApi(data),
    {
      manual: true,
      onSuccess: (response) => {
        if (response.code !== 0 || !response.data?.resetToken) {
          message.error(response.msg || "身份验证失败");
          return;
        }
        const accounts = response.data.accounts || [];
        if (accounts.length === 0) {
          message.error("未找到可重置密码的账号");
          return;
        }

        setResetToken(response.data.resetToken);
        setResetExpiresIn(response.data.expiresIn);
        setAccountCandidates(accounts);
        setVerificationToken("");
        setTypeStatus("default");
        setSeconds(null);
        form.setFieldsValue({ phoneCode: undefined });

        if (accounts.length === 1) {
          setSelectedAccountId(accounts[0].accountId);
          setCurrentStep(2);
        } else {
          setSelectedAccountId(null);
          setCurrentStep(1);
        }
      },
      onError: (error) => message.error("身份验证失败：" + error),
    }
  );

  const { run: runResetPassword, loading: resetPasswordLoading } = useRequest(
    (data: APITYPES.ResetPasswordParams) => resetPasswordApi(data),
    {
      manual: true,
      onSuccess: (response) => {
        if (response.code === 0) {
          setSuccessOpen(true);
          form.resetFields();
          setCurrentStep(0);
          setVerificationToken("");
          setTypeStatus("default");
          setSeconds(null);
          clearResetSession();
          runGetImageCaptcha();
        } else {
          message.error(response.msg || "密码重置失败");
        }
      },
      onError: (error) => message.error("密码重置失败：" + error),
    }
  );

  useEffect(() => {
    if (!originComing.current) {
      originComing.current = true;
      runGetImageCaptcha();
      runGetPublicKey();
    }
    return () => {
      originComing.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleWidth = () => {
      const width = document.documentElement.clientWidth;
      if (width <= 400) {
        setCardWidth("400px");
        setFormLayout("vertical");
      } else if (width < 680) {
        setCardWidth("100%");
        setFormLayout("vertical");
      } else if (width < 900) {
        setCardWidth("80%");
        setFormLayout("horizontal");
      } else {
        setCardWidth("60%");
        setFormLayout("horizontal");
      }
    };
    handleWidth();
    window.addEventListener("resize", handleWidth);
    return () => window.removeEventListener("resize", handleWidth);
  }, []);

  useEffect(() => {
    if (seconds && seconds > 0) {
      timer.current = setTimeout(() => setSeconds(seconds - 1), 1000);
    } else {
      setTypeStatus("default");
      clearTimeout(timer.current);
    }
    return () => clearTimeout(timer.current);
  }, [seconds]);

  const validatePasswordValue = (_rule: any, password: string) => {
    if (!password) return Promise.resolve();
    const result = validatePassword(password, watchPhone || "");
    if (!result.isValid) {
      return Promise.reject(new Error(result.errors.join("；")));
    }
    return Promise.resolve();
  };

  const validatePasswordAgain = (_rule: any, password: string) => {
    if (password && password !== watchPassword) {
      return Promise.reject(new Error("两次密码需保持一致！"));
    }
    if (password) return validatePasswordValue(_rule, password);
    return Promise.resolve();
  };

  const refreshCaptcha = () => {
    runGetImageCaptcha();
    form.setFieldsValue({ imgCode: undefined });
  };

  const handleSendMessage = async () => {
    try {
      await form.validateFields(["phone", "imgCode"]);
      if (!captchaId) {
        message.error("图片验证码已失效，请刷新后重试");
        return;
      }
      clearResetSession();
      setVerificationToken("");
      form.setFieldsValue({ phoneCode: undefined });
      setTypeStatus("check");
      setSeconds(60);
      runSendVerificationCode({
        ...(isInputEmail ? { email: watchPhone } : { phone: watchPhone }),
        captchaId,
        captcha: watchImgCode,
        action: "password_reset",
      });
    } catch {}
  };

  const handleIdentityNext = async () => {
    try {
      const values = await form.validateFields([
        "phone",
        "imgCode",
        "phoneCode",
      ]);
      if (!verificationToken) {
        message.error("请先获取验证码");
        return;
      }
      runVerifyPasswordResetIdentity({
        ...(isInputEmail ? { email: values.phone } : { phone: values.phone }),
        code: values.phoneCode,
        token: verificationToken,
      });
    } catch {}
  };

  const restartIdentity = () => {
    const contact = form.getFieldValue("phone");
    form.resetFields();
    form.setFieldsValue({ phone: contact });
    setCurrentStep(0);
    setVerificationToken("");
    setTypeStatus("default");
    setSeconds(null);
    clearResetSession();
    runGetImageCaptcha();
  };

  const handleAccountNext = () => {
    if (selectedAccountId === null) {
      message.error("请选择要重置密码的账号");
      return;
    }
    setCurrentStep(2);
  };

  const handleFinalReset = async () => {
    try {
      const values = await form.validateFields(["password", "againPassword"]);
      if (!resetToken || selectedAccountId === null) {
        message.error("重置会话已失效，请重新验证身份");
        restartIdentity();
        return;
      }
      if (!publicKeyObj) {
        message.error("公钥获取失败，请刷新页面重试");
        return;
      }

      const encryptedPassword = await encryptToVaultFormat(
        values.password,
        publicKeyObj
      );
      runResetPassword({
        password: encryptedPassword,
        resetToken,
        accountId: selectedAccountId,
      });
    } catch {}
  };

  const getParamsFromURL = useMemo(() => {
    const urlList = window.location.href.split("?");
    const urlStr = urlList.slice(1).join("?");
    return {
      goto: urlStr?.split("=")[0],
      redirectUrl: urlStr?.split("=").slice(1).join("="),
    };
  }, []);

  const closeSuccessModal = useCallback(() => {
    setSuccessOpen(false);
    if (
      getParamsFromURL.goto === "redirectUrl" &&
      getParamsFromURL.redirectUrl
    ) {
      const link = document.createElement("a");
      link.href = getParamsFromURL.redirectUrl;
      document.body.appendChild(link);
      link.click();
    }
  }, [getParamsFromURL]);

  const renderAccountDetails = (
    account: APITYPES.PasswordResetAccountCandidate
  ) => (
    <div>
      <div style={{ fontWeight: 500 }}>
        {account.enterpriseName || "未命名企业"}
      </div>
      <div>account_id：{account.accountId}</div>
      {account.userName && <div>账号用户名：{account.userName}</div>}
      {account.nickName && <div>账号昵称：{account.nickName}</div>}
    </div>
  );

  return (
    <div>
      <ConfigProvider>
        <Card
          title="重置密码"
          style={{
            width: cardWidth,
            margin: formLayout === "horizontal" ? "7% auto" : "0 auto",
            minWidth: 400,
          }}
          type="inner"
          bordered={formLayout === "horizontal"}
        >
          <Steps current={currentStep} style={{ marginBottom: 32 }}>
            <Steps.Step title="验证身份" />
            <Steps.Step title="选择企业" />
            <Steps.Step title="设置新密码" />
          </Steps>

          <Form form={form} {...formItemLayout} layout={formLayout}>
            {currentStep === 0 && (
              <>
                <Form.Item
                  label="手机号码/邮箱"
                  name="phone"
                  className={itemMarginBottom}
                  rules={[
                    { required: true, message: "请输入手机号码/邮箱" },
                    {
                      validator: (_rule, value: string) => {
                        const phoneReg = /^(1[3-9]\d{9})$/;
                        const emailReg =
                          /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
                        if (
                          value &&
                          !phoneReg.test(value) &&
                          !emailReg.test(value)
                        ) {
                          return Promise.reject(
                            new Error("请输入正确手机号/邮箱")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder="请输入手机号码/邮箱"
                    onChange={() => {
                      setVerificationToken("");
                      setTypeStatus("default");
                      setSeconds(null);
                      clearResetSession();
                      form.setFieldsValue({ phoneCode: undefined });
                    }}
                    disabled={identityVerifyLoading}
                  />
                </Form.Item>

                <Form.Item
                  className={itemMarginBottom}
                  label="图片验证码"
                  required
                >
                  <Row gutter={16}>
                    <Col span={10}>
                      <Form.Item
                        name="imgCode"
                        noStyle
                        rules={[
                          { required: true, message: "请输入图片验证码" },
                        ]}
                      >
                        <Input placeholder="请输入图片验证码" />
                      </Form.Item>
                    </Col>
                    <Col span={14}>
                      <img
                        src={imgSrc}
                        alt="图片加载中…"
                        style={{ width: 200, height: 40 }}
                      />
                      <Button
                        type="link"
                        style={{ verticalAlign: "top" }}
                        onClick={refreshCaptcha}
                      >
                        刷新
                      </Button>
                    </Col>
                  </Row>
                </Form.Item>

                <Form.Item
                  className={itemMarginBottom}
                  label={isInputEmail ? "邮箱验证码" : "手机验证码"}
                  required
                >
                  <Row gutter={16}>
                    <Col span={10}>
                      <Form.Item
                        name="phoneCode"
                        noStyle
                        rules={[
                          {
                            required: true,
                            message: isInputEmail
                              ? "请输入邮箱验证码"
                              : "请输入手机验证码",
                          },
                          {
                            pattern: /^\d{6}$/,
                            message: "请输入6位数字验证码",
                          },
                        ]}
                      >
                        <Input
                          placeholder={
                            isInputEmail
                              ? "请输入邮箱验证码"
                              : "请输入手机验证码"
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      {typeStatus === "default" ? (
                        <Button
                          type="primary"
                          onClick={handleSendMessage}
                          disabled={!watchPhone || !watchImgCode}
                          loading={sendCodeLoading}
                        >
                          发送验证码
                        </Button>
                      ) : (
                        <Button disabled>还剩{seconds}秒</Button>
                      )}
                    </Col>
                  </Row>
                </Form.Item>

                <div style={{ textAlign: "center" }}>
                  <Button
                    type="primary"
                    onClick={handleIdentityNext}
                    loading={identityVerifyLoading}
                  >
                    下一步
                  </Button>
                </div>
              </>
            )}

            {currentStep === 1 && (
              <>
                <Radio.Group
                  value={selectedAccountId}
                  onChange={(event) =>
                    setSelectedAccountId(event.target.value)
                  }
                  style={{ width: "100%" }}
                >
                  <Space direction="vertical" style={{ width: "100%" }}>
                    {accountCandidates.map((account) => (
                      <Radio
                        key={account.accountId}
                        value={account.accountId}
                        style={{
                          display: "flex",
                          width: "100%",
                          padding: 12,
                          border: "1px solid #f0f0f0",
                          borderRadius: 4,
                        }}
                      >
                        {renderAccountDetails(account)}
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
                <div style={{ textAlign: "center", marginTop: 24 }}>
                  <Space>
                    <Button onClick={restartIdentity}>重新验证身份</Button>
                    <Button type="primary" onClick={handleAccountNext}>
                      下一步
                    </Button>
                  </Space>
                </div>
              </>
            )}

            {currentStep === 2 && selectedAccount && (
              <>
                <Card
                  size="small"
                  title="当前重置账号"
                  style={{ marginBottom: 24 }}
                >
                  {renderAccountDetails(selectedAccount)}
                  {resetExpiresIn > 0 && (
                    <div style={{ color: "#8c8c8c", marginTop: 8 }}>
                      请在 {Math.floor(resetExpiresIn / 60)} 分钟内完成密码重置
                    </div>
                  )}
                </Card>
                <Form.Item
                  className={itemMarginBottom}
                  label="新密码"
                  name="password"
                  rules={[
                    { required: true, message: "请输入新密码" },
                    { validator: validatePasswordValue },
                  ]}
                >
                  <Input.Password placeholder="请输入新密码" />
                </Form.Item>
                <Form.Item
                  className={itemMarginBottom}
                  label="再次输入新密码"
                  name="againPassword"
                  dependencies={["password"]}
                  rules={[
                    { required: true, message: "请输入密码" },
                    { validator: validatePasswordAgain },
                  ]}
                >
                  <Input.Password placeholder="请再次输入新密码" />
                </Form.Item>
                <div style={{ textAlign: "center" }}>
                  <Space>
                    <Button
                      onClick={() =>
                        hasMultipleAccounts
                          ? setCurrentStep(1)
                          : restartIdentity()
                      }
                    >
                      {hasMultipleAccounts ? "返回选择企业" : "重新验证身份"}
                    </Button>
                    <Button
                      type="primary"
                      onClick={handleFinalReset}
                      loading={resetPasswordLoading}
                    >
                      确认重置
                    </Button>
                  </Space>
                </div>
              </>
            )}
          </Form>

          <Modal
            open={successOpen}
            onCancel={closeSuccessModal}
            footer={
              <Space>
                <Button onClick={closeSuccessModal}>关闭</Button>
                <Button danger onClick={closeSuccessModal}>
                  确定
                </Button>
              </Space>
            }
          >
            <p>
              <ExclamationCircleOutlined
                style={{ color: "#faad14", fontSize: 22 }}
              />
              <span style={successTextStyle}>您的密码已重置成功！</span>
            </p>
          </Modal>
        </Card>
      </ConfigProvider>
    </div>
  );
};

export default App;
